import type { PdfPageOptions } from '@kimuno/api-sdk';

import api from '../clients/api';
import { addInlineTableStyles } from '../components/utils/addInlineTableStyles';
import { createPrintHTMLDoc } from '../components/utils/createPrintHTMLDoc';
import services from '../services';
import { isSafari } from './browser';
import { notifyBugsnagHandledError } from './bugsnagClient';

/**
 * Triggers a browser download for a file that already has a URL
 * @param fileUrl - The URL of the file to download
 * @param name - The name of the downloaded file (including extension)
 */
export const downloadFile = (fileUrl: string, name: string) => {
	const link = document.createElement('a');
	link.href = fileUrl;
	link.download = name;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Triggers a browser download for a Blob by wrapping it in a temporary object URL
 * @param blob - The Blob to download
 * @param filename - The name of the downloaded file (including extension)
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	try {
		downloadFile(url, filename);
	} finally {
		URL.revokeObjectURL(url);
	}
};

/**
 * Rasterizes an SVG element to a PNG data URL at the given dimensions
 * @param svg - The SVG element to convert
 * @param width - The width of the resulting PNG
 * @param height - The height of the resulting PNG
 */
export const convertSvgToPng = (
	svg: SVGSVGElement,
	width: number,
	height: number
): Promise<string> => {
	return new Promise((resolve, reject) => {
		const svgSerialization = new XMLSerializer().serializeToString(svg);
		const svgBase64 = 'data:image/svg+xml;base64,' + btoa(svgSerialization);

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			reject('Failed to get 2D context');
			return;
		}

		const img = new Image();
		img.src = svgBase64;

		img.onload = () => {
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			const png = canvas.toDataURL('image/png');
			resolve(png);
		};

		img.onerror = () => {
			reject('Error loading SVG data');
		};
	});
};

/**
 * Downloads HTML content as a PDF file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 * @param accessToken - The public access token for the document
 * @param pageOptions - Page setup for the PDF (paper size, margins, footer)
 */
export const downloadHtmlAsPdf = async (
	html: string,
	filename: string,
	accessToken?: string,
	pageOptions?: PdfPageOptions
): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await services.pdf.convertHtmlToPdfBlob(styledHtml, accessToken, pageOptions);
		triggerBlobDownload(blob, `${filename}.pdf`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Downloads HTML content as a .docx file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 */
export const downloadHtmlAsDocx = async (html: string, filename: string): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await api.document.convertHtmlToDocx(styledHtml);
		triggerBlobDownload(blob, `${filename}.docx`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Opens a print dialog with HTML content
 * @param title - The title of the document
 * @param html - The HTML content to print
 * @returns false when the browser blocked the print window, so the caller can tell the user
 */
export const printHtmlDocument = (title: string, html: string): boolean => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const printDocument = createPrintHTMLDoc(title, styledHtml);

		// Open HTML in a new window and print
		const printWindow = window.open('', '_blank');
		if (!printWindow) {
			return false;
		}
		printWindow.document.write(printDocument);
		printWindow.document.close();

		// Wait for content to load, then trigger print
		printWindow.onload = () => {
			setTimeout(() => {
				printWindow.print();
			}, 250);
		};
		return true;
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

// One print frame at a time: a browser that never reports its print dialog
// closed keeps the frame until the next print replaces it. A timer could
// remove it while the dialog is still open.
let printFrame: { frame: HTMLIFrameElement; url: string } | null = null;

const removePrintFrame = () => {
	if (printFrame) {
		printFrame.frame.remove();
		URL.revokeObjectURL(printFrame.url);
		printFrame = null;
	}
};

const TAB_CLOSED_POLL = 1000;

// The tab reads the URL for as long as it stays open, and nothing reports
// its closing to the opener.
const revokeWhenClosed = (tab: Window, url: string) => {
	const poll = window.setInterval(() => {
		if (tab.closed) {
			window.clearInterval(poll);
			URL.revokeObjectURL(url);
		}
	}, TAB_CLOSED_POLL);
};

/**
 * On Safari, opens the tab `printPdfBlob` shows the PDF in. Call it in the
 * click handler before anything is awaited: Safari blocks a tab opened after
 * the click's activation has expired. Elsewhere it opens nothing.
 * @returns the tab, null when Safari blocked it, undefined where no tab is needed
 */
export const openPdfPrintTab = (): Window | null | undefined =>
	isSafari() ? window.open('', '_blank') : undefined;

/**
 * Opens the browser's print dialog for a PDF, from a hidden frame. Safari
 * cannot print an embedded PDF, so there the PDF opens in a tab instead.
 * @param blob - The PDF to print
 * @param tab - The tab from `openPdfPrintTab`; without it, Safari opens one now
 * @returns false when the browser blocked the tab, so the caller can tell the user
 */
export const printPdfBlob = (blob: Blob, tab?: Window | null): boolean => {
	const url = URL.createObjectURL(blob);
	if (isSafari()) {
		const target = tab === undefined ? window.open(url, '_blank') : tab;
		if (!target) {
			URL.revokeObjectURL(url);
			return false;
		}
		if (tab) {
			tab.location.href = url;
		}
		revokeWhenClosed(target, url);
		return true;
	}
	removePrintFrame();
	const frame = document.createElement('iframe');
	// Zero-sized rather than hidden: a frame with display:none prints blank.
	frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
	frame.onload = () => {
		// Runs after the caller returned, so a failure here can only be reported.
		try {
			frame.contentWindow?.addEventListener('afterprint', removePrintFrame, { once: true });
			frame.contentWindow?.print();
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
			removePrintFrame();
		}
	};
	frame.src = url;
	printFrame = { frame, url };
	document.body.appendChild(frame);
	return true;
};
